import React from 'react';
import type { DemoSchool } from '../api/simStudioApi';
import type { Theme } from './StudioHeader';
import { IconSchool, IconMapPin } from './Icons';

interface Props {
  schools: DemoSchool[];
  activeId: string | null;
  onSelect: (school: DemoSchool) => void;
  loading: boolean;
  theme: Theme;
}

export function SchoolSwitcher({ schools, activeId, onSelect, loading, theme }: Props) {
  const isDark = theme === 'dark';

  const labelColor   = isDark ? '#475569' : '#94a3b8';
  const skeleton     = isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)';
  const emptyColor   = isDark ? '#475569' : '#94a3b8';
  const cardBg       = isDark ? 'rgba(255,255,255,0.03)' : '#ffffff';
  const cardBorder   = isDark ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.07)';
  const cardText     = isDark ? '#e2e8f0' : '#1e293b';
  const cardSub      = isDark ? '#64748b' : '#94a3b8';
  const activeBg     = isDark ? 'rgba(124,58,237,0.12)' : 'rgba(124,58,237,0.06)';
  const activeBorder = 'rgba(124,58,237,0.35)';
  const activeShadow = isDark ? '0 0 18px rgba(124,58,237,0.18)' : '0 0 12px rgba(124,58,237,0.1)';
  const activeText   = isDark ? '#c4b5fd' : '#7c3aed';
  const activeSub    = isDark ? '#8b5cf6' : '#9f81d4';
  const activeAccent = '#7c3aed';
  const pillBg       = isDark ? 'rgba(124,58,237,0.1)' : 'rgba(124,58,237,0.06)';
  const pillBorder   = 'rgba(124,58,237,0.2)';
  const pillText     = isDark ? '#c4b5fd' : '#7c3aed';

  return (
    <div className="flex flex-col gap-2">
      <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: labelColor, paddingLeft: 2 }}>
        Escola Ativa
      </div>

      {loading && [1, 2].map(i => (
        <div key={i} style={{ height: 60, borderRadius: 12, background: skeleton }} className="animate-pulse" />
      ))}

      {!loading && schools.length === 0 && (
        <p style={{ fontSize: 12, color: emptyColor, lineHeight: 1.6 }}>
          Nenhuma escola demo encontrada.<br />Execute o Mega Seed primeiro.
        </p>
      )}

      {!loading && schools.map(school => {
        const isActive = school.id === activeId;
        return (
          <button
            key={school.id}
            onClick={() => onSelect(school)}
            className="w-full text-left transition-all cursor-pointer"
            style={{
              background: isActive ? activeBg : cardBg,
              border: `1px solid ${isActive ? activeBorder : cardBorder}`,
              borderRadius: 12,
              padding: '12px 14px',
              boxShadow: isActive ? activeShadow : 'none',
            }}
          >
            <div className="flex items-start gap-2.5">
              {/* Ícone */}
              <div style={{
                width: 34, height: 34, borderRadius: 8, flexShrink: 0, marginTop: 1,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                background: isActive
                  ? `rgba(124,58,237,${isDark ? '0.2' : '0.1'})`
                  : isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)',
              }}>
                <IconSchool size={16} color={isActive ? activeAccent : (isDark ? '#64748b' : '#94a3b8')} />
              </div>

              {/* Texto */}
              <div className="flex-1 min-w-0">
                <div style={{ fontSize: 13, fontWeight: 600, color: isActive ? activeText : cardText, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {school.nome}
                </div>
                <div className="flex items-center gap-1 mt-0.5">
                  <IconMapPin size={10} color={isActive ? activeSub : cardSub} />
                  <span style={{ fontSize: 11, color: isActive ? activeSub : cardSub }}>
                    {school.cidade}/{school.uf} · {school.totalAlunos} alunos
                  </span>
                </div>
              </div>
            </div>
          </button>
        );
      })}

      {activeId && schools.find(s => s.id === activeId) && (
        <div style={{
          marginTop: 2, padding: '8px 12px', borderRadius: 8,
          background: pillBg, border: `1px solid ${pillBorder}`,
          display: 'flex', alignItems: 'center', gap: 6,
        }}>
          <div style={{ width: 6, height: 6, borderRadius: '50%', background: pillText, flexShrink: 0 }} />
          <span style={{ fontSize: 11, fontWeight: 500, color: pillText, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {schools.find(s => s.id === activeId)?.nome}
          </span>
        </div>
      )}
    </div>
  );
}
